import React, { useState } from 'react'
import { Button } from 'primereact/button'
import { Panel } from 'primereact/panel'

import DatePicker from '../../../common/components/DatePicker'
import type { ChargePoint, MessageTrigger } from '../../../common/redux/api/web-api'
import {
    AvailabilityType,
    useChargePointPostChargingLimitationsMutation,
    useChargePointWhitelistMutation,
    useOcppCommandChangeAvailabilityMutation,
    useOcppCommandClearCacheMutation,
    useOcppCommandDataTransferMutation,
    useOcppCommandGetConfigurationMutation,
    useOcppCommandGetDiagnosticsMutation,
    useOcppCommandRemoteStartMutation,
    useOcppCommandRemoteStopMutation,
    useOcppCommandResetMutation,
    useOcppCommandTriggerMessageMutation,
    useOcppCommandUnlockConnectorMutation,
    useOcppCommandUpdateFirmwareMutation,
} from '../../../common/redux/api/web-api'
import { SFormRow, SLabelText } from '../../../common/styles/Form'
import { isChargingConnector } from '../../../lib/findChargingConnector'
import formatDate from '../../../lib/formatDate'
import { ResetType } from '../../../lib/reset-ocpp-charge-point'
import RowDivider from '../../../common/components/RowDivider'
import Text from '../../../ui/Text'
import useText from '../../../common/hooks/useText'
import { Dropdown } from '../../../common/uikit/Dropdown'
import { Input } from '../../../common/uikit/Input'
import { useToast } from '../../../common/uikit/ToastProvider'
import { confirm } from '../../../common/uikit/ConfirmDialog'

import { ConnectorHeader } from './charge-point-ocpp-commands-widget.style'

type MutationRequest = {
    unwrap: () => Promise<unknown>
}

export type ChargePointOcppCommandsWidgetProps = {
    chargePoint: ChargePoint
}

export enum OcppCommandTypesEnum {
    ChargerControls = 'ChargerControls',
    Connector = 'Connector Commands',
    GetDiagnostics = 'Diagnostics',
    UpdateFirmware = 'Update firmware',
    TriggerMessage = 'Trigger message',
}

type OcppCommandOptionValue = keyof typeof OcppCommandTypesEnum

export const OcppCommandoptions: Array<{ label: string; value: OcppCommandOptionValue }> = [
    { label: 'Charger Controls', value: 'ChargerControls' },
    { label: 'Connector Commands', value: 'Connector' },
    { label: 'Get Diagnostics', value: 'GetDiagnostics' },
    { label: 'Update firmware', value: 'UpdateFirmware' },
    { label: 'Trigger Message', value: 'TriggerMessage' },
]

export const triggerMessageOptions = [
    { label: 'Boot notification', value: 'BootNotification' },
    { label: 'Diagnostics status notification', value: 'DiagnosticsStatusNotification' },
    { label: 'Firmware status notification', value: 'FirmwareStatusNotification' },
    { label: 'Heartbeat', value: 'Heartbeat' },
    { label: 'Meter values', value: 'MeterValues' },
    { label: 'Status notification', value: 'StatusNotification' },
]

enum OperativityEnum {
    Inoperative = 'Inoperative',
    Operative = 'Operative',
}

export function ChargePointOcppCommandsWidget({ chargePoint }: ChargePointOcppCommandsWidgetProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const chargePointId = chargePoint.id as string
    const connectors = chargePoint.connectors ?? []
    const [authToken, setAuthToken] = useState<string[]>(new Array(connectors.length).fill(''))
    const [diagnosticsLocation, setDiagnosticsLocation] = useState<string>('')
    const [fwUpdateRetryInterval, setFwUpdateRetryInterval] = useState<undefined | number>(undefined)
    const [fwUpdateRetries, setFwUpdateRetries] = useState<undefined | number>(undefined)
    // date-time input accepts and works with format YYYY-MM-DDThh:mm
    const [fwRetrieveDate, setFwRetrieveDate] = useState<string>(formatDate(new Date(), "yyyy-MM-dd'T'hh:mm"))

    const [requestedMessage, setRequestedMessage] = useState<MessageTrigger | ''>('')
    const [updateFWLocation, setUpdateFWLocation] = useState('')
    const [selectedOcppCommandType, setSelectedOcppCommandType] = useState<OcppCommandOptionValue>('ChargerControls')
    const selectedOcppCommand = OcppCommandTypesEnum[selectedOcppCommandType]
    const [transactionId, setTransactionId] = useState<number[]>(new Array(connectors.length))

    const [getDiagnosticsReq] = useOcppCommandGetDiagnosticsMutation()
    const diagnostics = () => {
        sendRequest(
            OcppCommandTypesEnum.GetDiagnostics,
            getDiagnosticsReq({
                chargePointId,
                getDiagnosticsReq: { location: diagnosticsLocation },
            })
        )
    }

    const [updateFirmwareReq] = useOcppCommandUpdateFirmwareMutation()
    const updateFw = () => {
        sendRequest(
            OcppCommandTypesEnum.UpdateFirmware,
            updateFirmwareReq({
                chargePointId,
                updateFirmwareReq: {
                    location: updateFWLocation,
                    retrieveDate: new Date(fwRetrieveDate).toISOString(),
                    ...(fwUpdateRetries && { retries: fwUpdateRetries }),
                    ...(fwUpdateRetryInterval && { retryInterval: fwUpdateRetryInterval }),
                },
            })
        )
    }

    const [unlockConnectorReq] = useOcppCommandUnlockConnectorMutation()
    const unlockConnector = async (connectorId: number) => {
        sendRequest(
            text('btnUnlockConnector'),
            unlockConnectorReq({
                chargePointId,
                unlockConnectorReq: { connectorId: connectorId },
            })
        )
    }

    const [remoteStartReq] = useOcppCommandRemoteStartMutation()
    const remoteStart = async (connectorId: number) => {
        sendRequest(
            text('btnRemoteStart'),
            remoteStartReq({
                chargePointId,
                remoteStartTransactionReq: {
                    connectorId: connectorId,
                    idTag: authToken[connectorId - 1],
                },
            })
        )
    }

    const [remoteStopRequest] = useOcppCommandRemoteStopMutation()
    const remoteStop = (connectorId: number) => {
        sendRequest(
            text('btnRemoteStop'),
            remoteStopRequest({
                chargePointId,
                remoteStopTransactionReq: {
                    transactionId: transactionId[connectorId - 1],
                },
            })
        )
    }

    const [triggerMessageRequest] = useOcppCommandTriggerMessageMutation()
    const sendTriggerMessage = () => {
        if (!requestedMessage) return

        sendRequest(
            text('btnSendTriggerMessage'),
            triggerMessageRequest({
                chargePointId,
                triggerMessageReq: {
                    requestedMessage,
                },
            })
        )
    }

    const [resetRequest] = useOcppCommandResetMutation()
    const reset = (resetType: ResetType) => {
        sendRequest(
            text('msgResetChargePoint'),
            resetRequest({
                chargePointId,
                resetReq: {
                    type: resetType,
                },
            })
        )
    }

    const hardReset = async () => {
        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: <Text code="msgHardResetLabel" />,
            variant: 'danger',
        })

        if (confirmed) {
            reset(ResetType.HARD)
        }
    }

    const [dataTransferRequest] = useOcppCommandDataTransferMutation()
    const dataTransfer = (messageName: string, messageId: string) => {
        sendRequest(
            messageName,

            dataTransferRequest({
                chargePointId,
                dataTransferReq: {
                    vendorId: 'com.chargeamps',
                    messageId,
                },
            })
        )
    }

    const [postChargingLimitationsReq] = useChargePointPostChargingLimitationsMutation()
    const requestChargingLimitations = () => {
        sendRequest(
            text('btnRequestChargingLimitations'),
            postChargingLimitationsReq({
                chargePointId,
            })
        )
    }

    const [clearCacheRequest] = useOcppCommandClearCacheMutation()
    const clearCache = () => {
        sendRequest(
            text('btnClearCache'),
            clearCacheRequest({
                chargePointId,
            })
        )
    }

    const [unbanRequest] = useChargePointWhitelistMutation()
    const unban = () => {
        sendRequest(
            text('btnUnban'),
            unbanRequest({
                chargePointId,
                whitelistPayload: { durationInSeconds: 7200 },
            })
        )
    }

    const [getConfigurationRequest] = useOcppCommandGetConfigurationMutation()
    const getConfiguration = () => {
        sendRequest(
            text('btnGetConfig'),
            getConfigurationRequest({
                chargePointId,
                getConfigurationReq: {},
            })
        )
    }

    const [setAvailabilityRequest] = useOcppCommandChangeAvailabilityMutation()
    const setAvailability = (connector: number, type: AvailabilityType) => {
        sendRequest(
            `${text('lblInoperativeOrOperativePlaceholder')} ${type}`,
            setAvailabilityRequest({
                chargePointId,
                changeAvailabilityReq: {
                    connectorId: connector,
                    type,
                },
            })
        )
    }

    const sendRequest = async (requestType: string, request: MutationRequest) => {
        try {
            await request.unwrap()
            toast.notifySuccess(
                text('lblOcppCommandSent', {
                    commandName: requestType,
                })
            )
        } catch (exception) {
            toast.notifyError(text('lblOcppCommandSendError', { commandName: requestType }))
        }
    }

    const connectorCommands = (connectorName: string, isChargingConnector: boolean, key: number) => {
        const connectorNumber = key + 1
        return (
            <>
                {connectors.length > 1 && <ConnectorHeader>{connectorName}</ConnectorHeader>}
                {isChargingConnector && (
                    <div>
                        <SLabelText>{text('lblRemoteStartStop')}</SLabelText>
                        <div className="flex justify-content-end gap-2 my-3">
                            <Input
                                id="auth-token-input"
                                data-testid={`auth-token-input-${key}`}
                                label={text('lblAuthTokenPlaceholder')}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const authTokenClone: string[] = [...authToken]
                                    authTokenClone[key] = e.target.value
                                    setAuthToken(authTokenClone)
                                }}
                                value={authToken[key] ?? ''}
                            />
                        </div>
                        <div className="flex justify-content-end gap-2 my-3">
                            <Button
                                type="button"
                                onClick={() => remoteStart(connectorNumber)}
                                label={text('btnRemoteStart')}
                                icon="pi pi-play-circle"
                                data-testid={`remote-start-btn-${key}`}
                                disabled={!(authToken?.length && authToken[key])}
                            />
                        </div>
                        <div className="flex justify-content-end gap-2 my-3">
                            <Input
                                id="transaction-id-input"
                                data-testid={`transaction-id-input-${key}`}
                                label={text('lblTransactionIdPlaceholder')}
                                type="number"
                                min={0}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const transactionIdClone: number[] = [...transactionId]
                                    transactionIdClone[key] = Number(e.target.value)
                                    setTransactionId(transactionIdClone)
                                }}
                                value={transactionId[key] ?? ''}
                            />
                        </div>
                        <div className="flex justify-content-end gap-2 my-3">
                            <Button
                                type="button"
                                onClick={() => remoteStop(connectorNumber)}
                                label={text('btnRemoteStop')}
                                icon="pi pi-stop-circle"
                                data-testid={`remote-stop-btn-${key}`}
                                disabled={!(transactionId?.length && transactionId[key])}
                            />
                        </div>
                    </div>
                )}
                <div>
                    <SLabelText>{text('lblSetAvailability')} </SLabelText>
                    <div className="flex justify-content-end gap-2 my-3">
                        <Button
                            type="button"
                            onClick={() => setAvailability(connectorNumber, OperativityEnum.Inoperative)}
                            data-testid={`set-inoperative-btn-${key}`}
                            label={text('btnInoperative')}
                            icon="pi pi-ban"
                        />
                        <Button
                            type="button"
                            onClick={() => setAvailability(connectorNumber, OperativityEnum.Operative)}
                            data-testid={`set-operative-btn-${key}`}
                            label={text('btnOperative')}
                            icon="pi pi-check-circle"
                        />
                    </div>
                </div>
                {isChargingConnector && (
                    <div>
                        <SLabelText>{text('lblConnectorLock')}</SLabelText>
                        <div className="flex justify-content-end gap-2 my-3">
                            <Button
                                type="button"
                                onClick={() => unlockConnector(connectorNumber)}
                                data-testid={`unlock-connector-btn-${key}`}
                                label={text('lblUnlock')}
                                icon="pi pi-unlock"
                            />
                        </div>
                    </div>
                )}
            </>
        )
    }

    return (
        <Panel header={text('lblOcppCommandsWidget')} data-testid="ocpp-commands-widget">
            <form>
                <Dropdown
                    options={OcppCommandoptions.map((option) => ({ value: option.value, label: option.label }))}
                    id="ocpp-command"
                    data-testid="ocpp-command-select"
                    onChange={(event) => setSelectedOcppCommandType(event.value)}
                    value={selectedOcppCommandType}
                />

                <div className="mt-3 text-lg">
                    <div className="font-bold">{text('lblAvailableCommands')}</div>
                </div>
                <div>
                    <SFormRow>
                        {selectedOcppCommand === OcppCommandTypesEnum.GetDiagnostics ? (
                            <>
                                <Input
                                    id="diagnostics-url"
                                    data-testid="diagnostics-url-input"
                                    label={text('lblDiagnosticsLocationPlaceholder')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setDiagnosticsLocation(e.target.value)
                                    }
                                    value={diagnosticsLocation}
                                />
                                <div className="flex justify-content-end gap-2 my-3">
                                    <Button
                                        type="button"
                                        data-testid="get-diagnostics-btn"
                                        onClick={diagnostics}
                                        label={text('btnGetDiagnostics')}
                                        icon="pi pi-download"
                                        disabled={!diagnosticsLocation?.length}
                                    />
                                </div>
                            </>
                        ) : null}
                        {selectedOcppCommand === OcppCommandTypesEnum.UpdateFirmware ? (
                            <>
                                <Input
                                    id="firmware-url"
                                    data-testid="firmware-url-input"
                                    label={text('lblFirmwareUploadPlaceholder')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setUpdateFWLocation(e.target.value)
                                    }
                                    value={updateFWLocation}
                                />

                                <SLabelText>{text('lblRetrievalOptions')}</SLabelText>
                                <div className="flex justify-content-end gap-2 my-3">
                                    <div className="flex flex-column w-9">
                                        <span>Retrieval date</span>
                                        <DatePicker
                                            id={'retrieve-date'}
                                            type="datetime-local"
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setFwRetrieveDate(event.target.value)
                                            }}
                                            value={fwRetrieveDate}
                                        />
                                    </div>

                                    <div className="flex w-6 gap-2">
                                        <Input
                                            min={0}
                                            id="Retries"
                                            type="number"
                                            label="Retries"
                                            name="retries"
                                            className="text-right"
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                setFwUpdateRetries(
                                                    event.target.value ? Number(event.target.value) : undefined
                                                )
                                            }
                                            value={fwUpdateRetries ?? ''}
                                        />
                                        <Input
                                            min={0}
                                            id="RetryInterval"
                                            label="Retry interval"
                                            type="number"
                                            name="retryInterval"
                                            className="text-right"
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                setFwUpdateRetryInterval(
                                                    event.target.value ? Number(event.target.value) : undefined
                                                )
                                            }
                                            value={fwUpdateRetryInterval ?? ''}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-content-end gap-2 my-3">
                                    <Button
                                        type="button"
                                        data-testid="update-firmware-btn"
                                        onClick={updateFw}
                                        label={text('btnUpdateFirmware')}
                                        icon="pi pi-upload"
                                        disabled={!updateFWLocation.length}
                                    />
                                </div>
                            </>
                        ) : null}
                        {selectedOcppCommand === OcppCommandTypesEnum.TriggerMessage ? (
                            <>
                                <Dropdown
                                    value={requestedMessage}
                                    id={'trigger-message-select'}
                                    options={triggerMessageOptions}
                                    data-testid="trigger-message-select"
                                    label={text('lblPayloadPlaceholder')}
                                    onChange={(event) => setRequestedMessage(event.value as MessageTrigger)}
                                />
                                <div className="flex justify-content-end gap-2 my-3">
                                    <Button
                                        type="button"
                                        onClick={sendTriggerMessage}
                                        label={text('btnSendTriggerMessage')}
                                        icon="pi pi-send"
                                        disabled={!requestedMessage.length}
                                        data-testid="trigger-message-btn"
                                    />
                                </div>
                            </>
                        ) : null}
                        {selectedOcppCommand === OcppCommandTypesEnum.Connector &&
                            connectors.map((connector, index) => (
                                <div key={`connector-${connector.connectorId}`}>
                                    {connectorCommands(connector.name ?? '', isChargingConnector(connector), index)}
                                    {index + 1 < connectors.length && connectors.length > 1 && (
                                        <hr key={`hr-${index}`} />
                                    )}
                                </div>
                            ))}
                        {selectedOcppCommand === OcppCommandTypesEnum.ChargerControls ? (
                            <>
                                <SLabelText>{text('lblDataTransfer')}</SLabelText>
                                <div>
                                    <div className="flex justify-content-end gap-2 my-3">
                                        <Button
                                            type="button"
                                            data-testid="component-info-btn"
                                            onClick={() =>
                                                dataTransfer(text('btnComponentInformation'), 'componentInfo')
                                            }
                                            label={text('btnComponentInformation')}
                                            icon="pi pi-info-circle"
                                        />
                                        <Button
                                            type="button"
                                            data-testid="network-status-btn"
                                            onClick={() => dataTransfer(text('btnNetworkStatus'), `networkStatus`)}
                                            label={text('btnNetworkStatus')}
                                            icon="pi pi-wifi"
                                        />
                                        <Button
                                            type="button"
                                            onClick={() => dataTransfer(text('btnSsidList'), 'ssidList')}
                                            label={text('btnSsidList')}
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <RowDivider />
                                <SLabelText>{text('lblGeneralCommands')}</SLabelText>
                                <div>
                                    <div className="flex justify-content-end gap-2 my-3">
                                        <Button
                                            type="button"
                                            data-testid="clear-cache-btn"
                                            onClick={clearCache}
                                            label={text('btnClearCache')}
                                            icon="pi pi-trash"
                                        />
                                        <Button
                                            type="button"
                                            data-testid="get-config-btn"
                                            onClick={getConfiguration}
                                            label={text('btnGetConfig')}
                                            icon="pi pi-cog"
                                        />
                                        <Button
                                            type="button"
                                            data-testid="unban-btn"
                                            onClick={unban}
                                            label={text('btnUnban')}
                                            icon="pi pi-unlock"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-content-end gap-2 my-3">
                                    <Button
                                        type="button"
                                        data-testid="poll-charging-limitations-btn"
                                        onClick={requestChargingLimitations}
                                        label={text('btnRequestChargingLimitations')}
                                        icon="pi pi-bolt"
                                    />
                                </div>
                                <RowDivider />
                                <SLabelText>{text('lblChargerReset')}</SLabelText>
                                <div className="flex justify-content-end gap-2 my-3">
                                    <Button
                                        type="button"
                                        data-testid="reset-hard-btn"
                                        onClick={() => void hardReset()}
                                        label={text('btnResetHard')}
                                        icon="pi pi-refresh"
                                        severity="danger"
                                    />
                                    <Button
                                        type="button"
                                        data-testid="reset-soft-btn"
                                        onClick={() => reset(ResetType.SOFT)}
                                        label={text('btnResetSoft')}
                                        icon="pi pi-refresh"
                                    />
                                </div>
                            </>
                        ) : null}
                    </SFormRow>
                </div>
            </form>
        </Panel>
    )
}
